import { useState } from "react";
import { Divider, Menu, MenuItem } from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import { operational } from "@mideye/design-tokens";
import { MideyeTopBarAccountButton } from "@mideye/top-bar";
import { useAuth } from "@/auth/AuthContext";
import { setCompanyOverride } from "@/services/api";

const userMenuTokens = operational.dashboard.userMenu;

export default function UserMenu() {
  const {
    user,
    logout,
    effectiveCompanyId,
    setEffectiveCompanyId,
    availableCompanies,
  } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuOpen = (e: React.MouseEvent<HTMLElement>) =>
    setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleCompanyChange = (company: string) => {
    setEffectiveCompanyId(company);
    setCompanyOverride(company === user?.company_id ? null : company);
    handleMenuClose();
  };

  const handleLogout = () => {
    handleMenuClose();
    logout();
  };

  if (!user) return null;
  const isMenuOpen = Boolean(anchorEl);

  return (
    <>
      <UserMenuButton user={user} open={isMenuOpen} onClick={handleMenuOpen} />

      <Menu
        id="dashboard-user-menu"
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={handleMenuClose}
        data-testid="user-menu"
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <CompanyMenuSection
          isAdmin={user.is_admin}
          availableCompanies={availableCompanies}
          effectiveCompanyId={effectiveCompanyId}
          onCompanyChange={handleCompanyChange}
        />
        <MenuItem
          onClick={handleLogout}
          data-testid="sign-out-button"
          sx={{ color: userMenuTokens.signOut, fontSize: "0.9rem" }}
        >
          <LogoutIcon sx={{ mr: 1.5, fontSize: "1.1rem" }} />
          Sign Out
        </MenuItem>
      </Menu>
    </>
  );
}

function UserMenuButton({
  user,
  open,
  onClick,
}: {
  user: { name: string | null; email: string | null };
  open: boolean;
  onClick: (e: React.MouseEvent<HTMLElement>) => void;
}) {
  return (
    <MideyeTopBarAccountButton
      avatarLabel={user.name || user.email}
      detail={user.email}
      displayName={user.name || "User"}
      expanded={open}
      onClick={onClick}
    />
  );
}

function CompanyMenuSection({
  isAdmin,
  availableCompanies,
  effectiveCompanyId,
  onCompanyChange,
}: {
  isAdmin: boolean;
  availableCompanies: string[];
  effectiveCompanyId: string | null;
  onCompanyChange: (company: string) => void;
}) {
  if (!isAdmin || availableCompanies.length === 0) {
    return null;
  }

  return (
    <>
      <MenuItem
        disabled
        data-testid="company-select-label"
        sx={{
          pointerEvents: "none",
          color: userMenuTokens.textMuted,
          fontSize: "0.75rem",
          fontWeight: 500,
        }}
      >
        SELECT COMPANY
      </MenuItem>
      {availableCompanies.map((company) => (
        <MenuItem
          key={company}
          onClick={() => onCompanyChange(company)}
          selected={effectiveCompanyId === company}
          data-testid={`company-menu-item-${company}`}
          sx={{
            fontSize: "0.9rem",
            backgroundColor:
              effectiveCompanyId === company
                ? userMenuTokens.selectedBg
                : "transparent",
            color:
              effectiveCompanyId === company
                ? userMenuTokens.selectedFg
                : "inherit",
            fontWeight: effectiveCompanyId === company ? 600 : 400,
          }}
        >
          {company}
        </MenuItem>
      ))}
      <Divider sx={{ my: 1 }} />
    </>
  );
}
